import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  MapPin,
  Bookmark,
  Bell,
  Sparkles,
  Download,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { BentoCard } from "@/components/dashboard/BentoCard";

const SECTIONS = [
  {
    icon: MapPin,
    title: "1. Filter to find properties",
    steps: [
      "Click Properties in the sidebar.",
      "Choose a state (Nevada, Tennessee, Texas, New York, California, Arizona, Illinois).",
      "Pick a city from the whitelist — we only show the top cities per state so you're not drowning in noise.",
      "Optional: set a minimum/maximum tax owed, or filter by sale date range.",
    ],
    cta: { label: "Open Properties", href: "/properties" },
  },
  {
    icon: Bookmark,
    title: "2. Save your search",
    steps: [
      "Once you've dialed in the filters that matter to you, head to Alerts.",
      "Click New Alert, enter a name (e.g. \"Vegas commercial under $50k\"), paste your filter criteria, and add the email you want notifications sent to.",
      "You can save as many search combinations as you want — one per neighborhood, one per price band, etc.",
    ],
    cta: { label: "Open Alerts", href: "/alerts" },
  },
  {
    icon: Bell,
    title: "3. Get email alerts",
    steps: [
      "Every 30 minutes, Lien Scout re-scrapes each active county and diff-checks the results against your saved searches.",
      "The moment a new parcel matches, we email you the details — address, parcel #, tax owed, sale date, and a direct link into the app.",
      "You can pause any alert without deleting the search.",
    ],
  },
  {
    icon: Sparkles,
    title: "4. Read AI Deal Scores",
    steps: [
      "Each property card shows a Deal Score (0–100). Higher = more likely to be a bargain given assessed value vs. tax owed.",
      "The score factors in neighborhood market comps, redemption period, property type, and the amount you'd owe upfront.",
      "Sort Properties by Deal Score to skip the noise and see the standouts first.",
    ],
  },
  {
    icon: Download,
    title: "5. Export & take action",
    steps: [
      "Every filtered list can be exported to CSV or JSON so you can share it with your team, dump it into a spreadsheet, or feed it into your own analytics.",
      "Look up ownership records or bidding details on each county's official site (links included on every property).",
      "Redemption periods vary by county — read the notes on each property before you commit.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "6. About the data",
    steps: [
      "All data comes directly from official county Trustee, Clerk & Master, or Treasurer offices. We do not scrape private aggregators.",
      "Records refresh every 30 minutes, and every scrape logs to the Runs page so you can audit exactly what was pulled and when.",
      "This is public-record research, not investment advice. Always verify with the county before bidding.",
    ],
  },
];

export default function GettingStartedPage() {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    // Just a visual flourish on this page.
    setLogo(true);
  }, []);
  return (
    <div className="space-y-8">
      <div className="border-2 border-black bg-brand-black text-white px-8 py-10 shadow-brutal">
        <div className="flex items-center gap-3 font-mono-data text-[11px] uppercase tracking-[0.24em] text-yellow-400">
          <BookOpen size={14} strokeWidth={2.5} />
          Getting Started
        </div>
        <h1
          data-testid="getting-started-title"
          className="mt-3 font-brand text-4xl sm:text-5xl text-red-500 leading-none"
        >
          How to use Lien Scout
        </h1>
        <p className="mt-4 font-display text-sm sm:text-base text-zinc-300 max-w-xl">
          Six short sections that walk you through everything from your first
          filter to your first email alert. Total read time: about 4 minutes.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          return (
            <BentoCard
              key={section.title}
              className="!p-6"
              data-testid={`gs-section-${section.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "")}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 border-2 border-black bg-yellow-400 flex items-center justify-center shadow-brutal-sm">
                  <Icon size={18} strokeWidth={2.5} />
                </div>
                <h2 className="font-display font-bold text-lg leading-tight">
                  {section.title}
                </h2>
              </div>
              <ol className="list-decimal list-inside space-y-2 font-display text-sm text-zinc-700 leading-relaxed">
                {section.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              {section.cta && (
                <Link
                  to={section.cta.href}
                  data-testid={`gs-cta-${section.cta.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className="mt-5 inline-flex items-center gap-2 border-2 border-black bg-red-600 text-white px-3 py-1.5 font-display font-semibold text-sm shadow-brutal-sm hover:bg-red-500"
                >
                  {section.cta.label}
                  <ArrowRight size={14} strokeWidth={2.5} />
                </Link>
              )}
            </BentoCard>
          );
        })}
      </div>

      <div className="border-2 border-black bg-yellow-100 p-6 shadow-brutal-sm">
        <h3 className="font-display font-bold text-lg">
          Still need help?
        </h3>
        <p className="mt-1 font-display text-sm text-zinc-700">
          Reply to any Lien Scout email and it lands in our team's inbox, or
          head to the{" "}
          <Link
            to="/contact"
            className="underline underline-offset-2 hover:text-red-600"
          >
            Contact page
          </Link>
          . We usually reply within a business day.
        </p>
      </div>
    </div>
  );
}
